'use client'

import { useRef, useState } from 'react'

/**
 * The /services service folio -- an ARIA tablist, and the only part of that
 * page needing client state. The mock drove it imperatively (setAttribute +
 * `panel.hidden`); here the selected panel is part of the render instead of a
 * DOM mutation. Roving tabindex and the arrow / Home / End keys are kept,
 * including the mock's two-column pairing under 760px.
 *
 * Split out of `ServicesContent` so the rest of the page can stay a Server
 * Component: the static sections are neither shipped nor hydrated.
 *
 * The four names and feature lists live here and mirror the service table in
 * `src/app/llms.txt/route.ts` -- change both together or the AI-facing answer
 * drifts from the page.
 *
 * PRICES DO NOT LIVE HERE. They arrive as a prop, resolved on the server by
 * `ServicesContent` from `src/data/services-knowledge.json` via
 * `getServicePriceModel`, which is the same source the per-service pages and
 * their schema.org Offers read. They used to be hardcoded in this array AND
 * again in `ServicesContent`, so /services stated every price twice and
 * neither copy was the canonical one (2026-09-20 heuristic evaluation, F16).
 * The prop keeps the 13KB knowledge JSON out of this client bundle.
 */

const SERVICES = [
  {
    service: 'visibility',
    priceKey: 'ai-visibility-strategy',
    index: '01 · Visibility',
    title: 'AI Visibility Strategy',
    desc: 'Get cited by ChatGPT, Claude, Perplexity, and Gemini. Audit, implementation, and monitoring with Radar and proven GEO methodology.',
    items: [
      '13-tool Radar audit with expert analysis',
      'Structured data and llms.txt implementation',
      'Hallucination detection and brand disambiguation',
      'Ongoing citation monitoring and optimization',
    ],
    href: '/services/ai-visibility-strategy',
  },
  {
    service: 'product',
    priceKey: 'ai-product-development',
    index: '02 · Product',
    title: 'AI Product Development',
    desc: 'From validated idea to production with AI integrated. Design, code, deployment, the whole thing, in a 90-day launch window.',
    items: [
      'New AI products from scratch',
      'Add AI to existing products',
      'Interface optimization and CRO',
      'You own the application code',
    ],
    href: '/services/ai-product-development',
  },
  {
    service: 'brand',
    priceKey: 'revenue-first-design',
    index: '03 · Brand',
    title: 'Brand and Sales Design',
    desc: 'Brand identity, design system, component library, pitch decks, and sales collateral shipped as one system, so your product looks as good as it works.',
    items: [
      'Brand identity and logo design',
      'Design system and component library',
      'Pitch decks and sales collateral',
      'Investor and enablement materials',
    ],
    href: '/services/revenue-first-design',
  },
  {
    service: 'growth',
    priceKey: 'ai-powered-growth',
    index: '04 · Growth',
    title: 'Growth Marketing',
    desc: 'Content, lead scoring, email automation, and analytics on a retainer. A repeatable way to generate qualified leads every month.',
    items: [
      'Content marketing and SEO',
      'Lead scoring and automation',
      'Email nurture sequences',
      'Revenue attribution dashboard',
    ],
    href: '/services/ai-powered-growth',
  },
] as const

/**
 * The canonical price, or a neutral fallback when the knowledge JSON has none.
 *
 * The raw lookup rendered `undefined` as empty text, so a missing or malformed
 * starting_price silently erased the advertised price from the tab and the
 * active panel rather than showing anything (Qodo round 1, finding 6).
 */
function priceLabel(prices: Record<string, string>, key: string) {
  return prices[key] ?? 'See pricing'
}

export default function ServicesFolio({
  prices,
}: {
  /** Canonical "From $X" strings keyed by services-knowledge id. */
  prices: Record<string, string>
}) {
  const [active, setActive] = useState(0)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])

  const focusTab = (i: number) => {
    setActive(i)
    tabRefs.current[i]?.focus()
  }

  const onTabKey = (e: React.KeyboardEvent, i: number) => {
    // Under 760px the tabs sit in a 2x2 grid, so left/right pair across a row
    // and up/down jump a row -- the mock's own mapping.
    const grid = window.matchMedia('(max-width: 760px)').matches
    const last = SERVICES.length - 1
    let next: number | null = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      next = grid
        ? i % 2 === 0
          ? i + 1
          : i - 1
        : (i + (e.key === 'ArrowRight' ? 1 : last)) % SERVICES.length
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      next = grid
        ? (i + 2) % SERVICES.length
        : (i + (e.key === 'ArrowDown' ? 1 : last)) % SERVICES.length
    } else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = last
    if (next === null) return
    e.preventDefault()
    focusTab(next)
  }

  return (
    <div className='svc-folio'>
      <div
        className='svc-folio-tabs'
        role='tablist'
        aria-label='Choose a Pixelmojo service'
      >
        {SERVICES.map((s, i) => (
          <button
            key={s.service}
            type='button'
            ref={el => {
              tabRefs.current[i] = el
            }}
            className='svc-folio-tab'
            id={`tab-${s.service}`}
            data-service={s.service}
            role='tab'
            aria-selected={i === active}
            aria-controls={`panel-${s.service}`}
            tabIndex={i === active ? 0 : -1}
            onClick={() => setActive(i)}
            onKeyDown={e => onTabKey(e, i)}
          >
            <span>{s.index}</span>
            <span>{priceLabel(prices, s.priceKey)}</span>
          </button>
        ))}
      </div>
      <div className='svc-folio-stage'>
        {SERVICES.map((s, i) => (
          <article
            key={s.service}
            className='svc-brief'
            id={`panel-${s.service}`}
            data-service={s.service}
            role='tabpanel'
            aria-labelledby={`tab-${s.service}`}
            tabIndex={0}
            hidden={i !== active}
          >
            <div className='svc-brief-meta'>
              <span className='svc-brief-index svc-label'>
                <i aria-hidden='true' />
                {s.index}
              </span>
              <span className='svc-label'>
                {priceLabel(prices, s.priceKey)}
              </span>
            </div>
            <h2 className='svc-display'>{s.title}</h2>
            <p className='svc-brief-desc'>{s.desc}</p>
            <ul className='svc-brief-list'>
              {s.items.map(item => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <a className='svc-brief-link' href={s.href}>
              Learn more <span aria-hidden='true'>→</span>
            </a>
          </article>
        ))}
      </div>
    </div>
  )
}
